<template>
  <div class="screen-groups-list has-tab-bar">
    <AppBar title="Groepen" :show-back-button="false" />
    <div class="container">
      <TabNavigation :tabs="tabs" :active-tab="activeTab" @toggle="toggleTab" />

      <!-- Ontdek groepen tab -->
      <template v-if="activeTab === 'explore'">
        <div class="flex m-0 py-4 justify-between space-x-4">
          <div class="w-1/2">
            <Dropdown
              class="mb-4"
              :disabled="false"
              :items="availableCities"
              :selected="selectedCity"
              empty-option="Alle steden"
              theme="rounded"
              @on-select="selectCity"
            />
          </div>
          <div class="w-1/2">
            <Dropdown
              class="mb-4"
              :disabled="false"
              :items="availableActivities"
              :selected="selectedActivity"
              empty-option="Alle sporten"
              theme="rounded"
              @on-select="(val) => selectActivity(parseInt(val))"
            />
          </div>
        </div>
        <hr class="my-3.5 border-gray-300" />
        <div class="d-flex flex-column">
          <GroupItem
            v-for="group in exploreGroups"
            :key="group.groupId"
            view="list"
            :name="group.name"
            :city="group.city"
            :member-count="group.memberCount"
            :image-url="group.imageUrl"
            @click.native="onExploreGroupClick(group.groupId)"
          />
        </div>
        <InfiniteLoading :identifier="infiniteId" @infinite="infiniteHandler">
          <span slot="no-more"></span>
          <span slot="no-results">
            <p class="text-sm mb-8">
              Er zijn geen groepen gevonden. Kies een andere sport of een andere
              locatie.
            </p>
          </span>
        </InfiniteLoading>
      </template>

      <!-- Mijn groepen tab -->
      <template v-if="activeTab === 'mine'">
        <template v-if="isAuthenticated">
          <div class="title-with-button">
            <h2 class="page-title">Jouw groepen</h2>
            <a
              href="#"
              class="btn btn-green btn-round btn-small btn-float"
              @click.prevent="$router.push('/groups/new')"
            >
              maak groep
            </a>
          </div>
          <SpinnerLoader v-if="loadingJoined" />
          <template
            v-else-if="
              groupsJoined && groupsJoined.data && groupsJoined.data.length
            "
          >
            <div class="d-flex flex-column">
              <GroupItem
                v-for="group in groupsJoined.data"
                :key="group.id"
                view="list"
                :name="group.name"
                :city="group.city"
                :member-count="group.acceptedMembers"
                :image-url="group.imageUrl"
                @click.native="onMineGroupClick(group.id)"
              />
            </div>
          </template>
          <template v-else>
            <div class="noGroups">
              Je zit nog niet in een groep. Meld je aan bij een bestaande groep
              of start een nieuwe.
            </div>
          </template>
        </template>
        <template v-else>
          <div class="noGroups mt-4">Log in om je groepen te zien.</div>
        </template>
      </template>
    </div>
    <TabBar selected="groups" />
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import TabBar from '@/components/TabBar'
import TabNavigation from '@/components/TabNavigation'
import SpinnerLoader from '@/components/SpinnerLoader'
import GroupItem from '@/components/GroupItem'
import Dropdown from '@/components/Dropdown'
import InfiniteLoading from 'vue-infinite-loading'
import {
  GROUP_EXPLORE,
  GROUP_EXPLORE_RESET,
  GROUP_GET_JOINED,
  GROUP_RESET_STATE
} from '@/store/actions/group'
import { mapState } from 'vuex'
import analytics from '@/utils/analytics'

export const VIEW_ALL = 'view_all'
export const VIEW_OWN = 'view_own'

export default {
  name: 'GroupsOverview',
  components: {
    AppBar,
    TabBar,
    TabNavigation,
    SpinnerLoader,
    GroupItem,
    Dropdown,
    InfiniteLoading
  },
  data() {
    return {
      activeTab: 'explore',
      selectedCity: '',
      selectedActivity: '',
      infiniteId: +new Date(),
      limit: 10,
      loadingJoined: false,
      joinedLoaded: false
    }
  },
  computed: {
    ...mapState({
      exploreGroups: (state) => state.group.explore.groups,
      exploreCount: (state) => state.group.explore.count,
      skip: (state) => state.group.explore.skip,
      availableCities: (state) =>
        state.group.explore.cities
          ? state.group.explore.cities.map((c) => ({
              key: c.city,
              value: `${c.city} (${c.count})`
            }))
          : [],
      availableActivities: (state) =>
        state.group.explore.activities
          ? state.group.explore.activities.map((a) => ({
              key: a.id,
              value: `${a.title} (${a.count})`
            }))
          : [],
      groupsJoined: (state) => state.group.groupsJoined,
      joinedGroup: (state) => state.group.joinedGroup,
      createdGroup: (state) => state.group.createdGroup,
      isAuthenticated: (state) => !!state.auth.token
    }),
    tabs() {
      return [
        { key: 'explore', value: 'Ontdek groepen' },
        { key: 'mine', value: 'Mijn groepen' }
      ]
    }
  },
  watch: {
    selectedCity() {
      this.$store.commit(GROUP_EXPLORE_RESET)
      this.infiniteId += 1
    },
    selectedActivity() {
      this.$store.commit(GROUP_EXPLORE_RESET)
      this.infiniteId += 1
    }
  },
  created() {
    this.$store.commit(GROUP_EXPLORE_RESET)

    const { city, activity } = this.$route.query
    if (city) this.selectedCity = city
    if (activity) this.selectedActivity = parseInt(activity) || ''

    if (this.joinedGroup || this.createdGroup) {
      this.$store.dispatch(GROUP_RESET_STATE)
    }
  },
  methods: {
    toggleTab(tab) {
      this.activeTab = tab
      analytics.capture('groups_tab_switched', { tab })
      if (tab === 'mine' && !this.joinedLoaded) {
        this.loadJoinedGroups()
      }
    },
    async loadJoinedGroups() {
      this.loadingJoined = true
      await this.$store.dispatch(GROUP_GET_JOINED, { limit: 0 })
      this.loadingJoined = false
      this.joinedLoaded = true
    },
    selectCity(city) {
      this.selectedCity = city
      analytics.capture('groups_explore_filtered', {
        filter_type: 'city',
        filter_value: city
      })
    },
    selectActivity(activity) {
      if (typeof activity === 'number' && !isNaN(activity)) {
        this.selectedActivity = activity
      } else {
        this.selectedActivity = ''
      }
      analytics.capture('groups_explore_filtered', {
        filter_type: 'activity',
        filter_value: activity
      })
    },
    onExploreGroupClick(groupId) {
      analytics.capture('groups_explore_group_clicked', { group_id: groupId })
      this.$router.push(`/groups/${groupId}`)
    },
    onMineGroupClick(groupId) {
      analytics.capture('groups_mine_group_clicked', { group_id: groupId })
      this.$router.push(`/groups/${groupId}`)
    },
    async infiniteHandler($state) {
      const result = await this.$store.dispatch(GROUP_EXPLORE, {
        skip: this.skip,
        limit: this.limit,
        city: this.selectedCity,
        activity: this.selectedActivity
      })
      if (result.length) {
        $state.loaded()
      } else {
        $state.complete()
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/css/variables';
.title-with-button {
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
}
.page-title {
  flex: 1;
  margin: 1rem 0;
}
.btn-float {
  width: 130px;
}
.noGroups {
  margin: 1rem 0;
}
</style>
